// A SENDER RULE (Rules → Companies): one address or `@domain`, and the two
// facts about it that live in two tables — may it email documents into intake
// (`intake_senders`), and, for a domain, which company its documents file to
// (`company_email_domains`). The tables stay two; `PUT /sender-rules` writes
// both sides of one rule in ONE transaction (code review G-01, D-275).
//
// One rule here, used by the route that reactivates a rule and by the screen
// that shows what an Inactive rule would do when made Active again, so the two
// cannot disagree.

/** The fields of either table's row this rule reads. */
interface SenderRuleSide {
  is_active: boolean
  retired_at?: string | null
}

function retiredMs(row: SenderRuleSide | null): number {
  if (row === null || !row.retired_at) return Number.NaN
  return Date.parse(row.retired_at)
}

/** The most recently made-Inactive row of one side. A row with no
    `retired_at` counts as oldest. */
function latestInactive<T extends SenderRuleSide>(rows: readonly T[]): T | null {
  let best: T | null = null
  for (const row of rows) {
    if (row.is_active) continue
    if (best === null) {
      best = row
      continue
    }
    const a = retiredMs(row)
    const b = retiredMs(best)
    if (Number.isNaN(b) || (!Number.isNaN(a) && a >= b)) best = row
  }
  return best
}

/**
 * THE ROWS AN INACTIVE RULE HELD WHEN IT WENT INACTIVE — what making it Active
 * puts back. `null` on both sides while either side has an Active row.
 *
 * On each side, the most recently made-Inactive row, kept only if it went
 * Inactive IN THE SAME WRITE as the other side's: the same `retired_at`,
 * exactly. `PUT /sender-rules` makes both rows Inactive in one transaction, and
 * Postgres stamps both with the transaction's own `now()`, so the two values are
 * equal by construction. A side switched off by an earlier edit carries an
 * earlier stamp and is not switched back on. (This replaced a 60-second window
 * that guessed at the same thing from two separate writes.)
 *
 * A side with no stamp at all is kept only when the other side has none either
 * — there is then nothing to tell them apart by.
 */
export function dormantSenderRows<I extends SenderRuleSide, R extends SenderRuleSide>(
  intake: readonly I[],
  routing: readonly R[],
): { intake: I | null; routing: R | null } {
  if (intake.some((row) => row.is_active) || routing.some((row) => row.is_active)) {
    return { intake: null, routing: null }
  }
  const i = latestInactive(intake)
  const r = latestInactive(routing)
  const ti = retiredMs(i)
  const tr = retiredMs(r)
  if (Number.isNaN(ti) && Number.isNaN(tr)) return { intake: i, routing: r }
  if (Number.isNaN(ti)) return { intake: null, routing: r }
  if (Number.isNaN(tr)) return { intake: i, routing: null }
  const latest = Math.max(ti, tr)
  return { intake: ti === latest ? i : null, routing: tr === latest ? r : null }
}

/**
 * THE KEY A SENDER RULE IS STORED UNDER: trimmed and lowercased, and a bare
 * `example.com` gains its `@`. One copy for the route that stores it and the
 * editor that says "Will be stored as …" (QA 2026-10-04 A-01). Blank stays blank.
 */
export function normalizeSenderKey(raw: string): string {
  const t = raw.trim().toLowerCase()
  if (t === '' || t.includes('@')) return t
  return `@${t}`
}

/**
 * The refusal for a second Active rule on one key, worded as Vendors and
 * Aliases word theirs. The route sends it as `duplicate_key`; the editor shows
 * the same sentence before Save (QA 2026-10-04 A-02).
 */
export function senderRuleTaken(key: string): string {
  return `Already in use: “${key}” already has an Active rule. Edit that rule rather than adding a second one.`
}
